import { createRoute, useNavigate, useParams } from "@tanstack/solid-router";
import { queryKeys, readingInkOn } from "@bso/shared";
import { appRoute } from "./route";
import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import {
  ALargeSmall,
  Bookmark,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  StickyNote,
  Superscript,
} from "@fleet/icons";
import {
  abbrToId,
  bookChapters,
  getBookNameDescriptor,
  getNextChapter,
  getPrevChapter,
  idToAbbr,
  isValidReference,
} from "@/utils/bible-books-data";
import { NotFoundState } from "@/components/ui/not-found-state";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { takeInitialSearchParam } from "@/lib/initial-search";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { type ReaderFontSize, readerStore } from "@/store/reader-store";
import { parseVerseSelection, serializeVerseSelection } from "@/utils/verse-selection";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { createPowerSyncQuery } from "@bso/shared/solid";
import {
  and,
  bible_bookmark,
  bible_verse_highlight,
  bible_version,
  eq,
  isNull,
} from "@bso/powersync";
import { formatVerseText } from "@/utils/verse-text";
import { computeHighlightBlocks } from "@/utils/highlight-blocks";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { VerseActionsBar } from "@/components/bible/verse-actions-bar";
import {
  loadChapter,
  ReaderNavSelector,
  type ReaderNavStep,
  type ReaderNavTarget,
  StrongConcordancePanel,
} from "@/features/reader";
import { createGestureRecognizer } from "@/lib/gestures/recognizer";
import { mergePointerHandlers, pointerHandlers } from "@/lib/gestures/solid";
import { vibrate } from "@/lib/gestures/haptics";
import { READER_SWIPE } from "@/lib/gestures/presets";
import { useReaderPullNav } from "@/features/reader/hooks/use-reader-pull-nav";
import { AppErrorBoundary } from "@/components/app-error-boundary";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
}

type ReaderCompose = "note" | "highlight";

type BibleSearch = {
  verse?: number;
  /**
   * Strong code open in the concordance panel. Makes the
   * reading with its Strong word open **shareable** (e.g. `/bible/kjv/gen/1?strong=H7225`):
   * reloading/pasting the URL reopens the panel exactly at that code. Empty =
   * panel closed, without dirtying URL.
   */
  strong?: string;
  /**
   * Active verse selection, compact form `4-6,9`. Makes the **selection
   * shareable**: pasting the URL reselects the range and reopens the action
   * bar. Absent = no selection.
   */
  sel?: string;
  /**
   * Reader font size (`small|medium|large`). View mode that travels
   * in the URL to reproduce the reading exactly. Absent = local preference
   * (localStorage).
   */
  fs?: ReaderFontSize;
  /**
   * Strong-number visibility. `0` = hidden, `1` = visible. Absent =
   * local preference. Canonical NUMBER form so TanStack serializes the
   * clean deep link (`?strongs=0`, without JSON quotes) and, when read back, the
   * router's `JSON.parse` returns a number that coercion accepts.
   */
  strongs?: 0 | 1;
  /**
   * Editor open over the selection: `note` (note) or `highlight` (highlight
   * tags). Reopens the form when pasting/reloading the URL. Absent = closed.
   */
  compose?: ReaderCompose;
  /**
   * Navigation selector (book → chapter → verse) open and its **specific
   * step**, so EACH drill-down step has its own copyable URL that
   * reopens exactly that screen in another browser:
   *   - `book`            → popup open at the Book step.
   *   - `book:gen`        → Chapter step for the selected book (Genesis).
   *   - `book:gen:4`      → Verse step for the selected chapter (Genesis 4).
   * Absent = popup closed. The final destination (choosing chapter/verse) navigates to
   * the reader URL and clears this parameter.
   */
  nav?: string;
};

const STRONG_CODE_PATTERN = /^[HG]\d+$/;

/** Normalizes/validates a Strong code from the URL (omits invalid values). */
function parseStrongParam(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const code = value.trim().toUpperCase();
  return STRONG_CODE_PATTERN.test(code) ? code : undefined;
}

/** Sanitizes `?fs=` to a valid font size (omits invalid values). */
function parseFontSizeParam(value: unknown): ReaderFontSize | undefined {
  return value === "small" || value === "medium" || value === "large" ? value : undefined;
}

/** Sanitizes `?compose=` to the valid editor (omits invalid values). */
function parseComposeParam(value: unknown): ReaderCompose | undefined {
  return value === "note" || value === "highlight" ? value : undefined;
}

/**
 * Sanitizes `?nav=` to a valid navigation step. Format `book[:abbr[:chapter]]`.
 * Any out-of-range book/chapter degrades to the previous valid step (does
 * not open a broken view), so an invalid id never produces a broken drill-down.
 */
function parseNavParam(value: unknown): ReaderNavStep | undefined {
  if (typeof value !== "string" || value.length === 0) {
    return undefined;
  }
  const [head, abbrRaw, chapterRaw] = value.split(":");
  if (head !== "book") {
    return undefined;
  }
  if (abbrRaw === undefined) {
    return { tab: "book" };
  }
  const abbr = abbrRaw.trim().toLowerCase();
  const bookId = abbrToId(abbr);
  // invalid abbr → degrade to the book step (popup open, no selection).
  // (`abbrToId` falls back to Genesis for an unknown id; the round-trip detects it).
  if (idToAbbr(bookId) !== abbr) {
    return { tab: "book" };
  }
  if (chapterRaw === undefined) {
    return { tab: "chapter", bookAbbr: abbr };
  }
  const chapter = Number.parseInt(chapterRaw, 10);
  const maxChapter = bookChapters[bookId] ?? 0;
  // Chapter out of range → degrade to the chapter step of the selected book.
  if (!Number.isFinite(chapter) || chapter < 1 || chapter > maxChapter) {
    return { tab: "chapter", bookAbbr: abbr };
  }
  return { tab: "verse", bookAbbr: abbr, chapter };
}

/** Serializes a navigation step to the canonical `?nav=` form. */
function serializeNavStep(step: ReaderNavStep): string {
  if (step.tab === "book" || !step.bookAbbr) {
    return "book";
  }
  if (step.tab === "chapter" || step.chapter === undefined) {
    return `book:${step.bookAbbr}`;
  }
  return `book:${step.bookAbbr}:${step.chapter}`;
}

export const bibleReaderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/bible/$version/$bookAbbr/$chapter",
  validateSearch: (search: Record<string, unknown>): BibleSearch => {
    const result: BibleSearch = {};
    const verseNum = search.verse ? Number(search.verse) : Number.NaN;
    if (Number.isFinite(verseNum) && verseNum > 0) {
      result.verse = verseNum;
    }
    const strong = parseStrongParam(search.strong);
    if (strong) {
      result.strong = strong;
    }
    // Re-serialize to the canonical form (`4-6,9`) so the URL is stable.
    const selVerses = parseVerseSelection(search.sel);
    if (selVerses.length > 0) {
      result.sel = serializeVerseSelection(selVerses);
    }
    const fs = parseFontSizeParam(search.fs);
    if (fs) {
      result.fs = fs;
    }
    // Canonical NUMBER form 0|1. Accept both a number and a string on INPUT
    // ("0"/"1"/0/1): TanStack auto-parses `?strongs=0` as NUMBER 0 via JSON.parse,
    // but a hand-written link or an older form may arrive as a string.
    // ALWAYS normalize to a number → TanStack serializes `?strongs=0` cleanly (without
    // JSON quotes) and, when read back, the number round-trips and coercion accepts it.
    // (CC-18: the original bug was that coercion REJECTED the number; now the
    // number IS the canonical form, so the parameter never falls back to `undefined` or the
    // router deletes it during boot → the `?strongs=` deep link stabilizes and the
    // restore-poll no longer re-navigates in loop.)
    if (search.strongs === 0 || search.strongs === "0") {
      result.strongs = 0;
    } else if (search.strongs === 1 || search.strongs === "1") {
      result.strongs = 1;
    }
    const compose = parseComposeParam(search.compose);
    if (compose) {
      result.compose = compose;
    }
    // Re-serialize `?nav=` to its canonical form (`book`, `book:gen`,
    // `book:gen:4`) so each selector step has a stable URL.
    const navStep = parseNavParam(search.nav);
    if (navStep) {
      result.nav = serializeNavStep(navStep);
    }
    return result;
  },
  component: BibleReaderPage,
});

function BibleReaderPage() {
  const SCROLL_DELAY_MS = 200;
  const params = useParams({ from: "/_app/bible/$version/$bookAbbr/$chapter" });
  const navigate = useNavigate();

  const { t } = useI18n();
  const search = bibleReaderRoute.useSearch();
  const version = () => params().version;
  const bookAbbr = () => params().bookAbbr;
  const chapter = () => params().chapter;
  const bookId = () => abbrToId(bookAbbr());
  const bookName = () => t(getBookNameDescriptor(bookId()));

  // A URL with an invalid book or chapter (`/bible/kjv/zzz/3`, `/bible/kjv/gen/999`)
  // previously coerced to Genesis 1 / empty chapter, leaving the reader broken or loading.
  // We detect the invalid reference and show "no encontrado" with a safe fallback.
  const validReference = createMemo(() => isValidReference(bookAbbr(), chapter()));

  // Database contexts
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;

  // Book → Chapter → Verse selector. Its ACTIVE step lives in the URL (`?nav=`) so
  // that EACH drill-down step (popup open, book-chapter, chapter-verse)
  // has its own copyable URL that reopens exactly that screen in
  // another browser. The selector component (feature `reader`) is controlled:
  // it reads the step from `navStep()` and step advances write to the URL.
  const navStep = (): ReaderNavStep | undefined => parseNavParam(search().nav);
  const isBookSelectorOpen = () => navStep() !== undefined;

  /** Opens/advances the selector by writing the step to the URL (without bloating history). */
  function setNavStep(step: ReaderNavStep | undefined) {
    void navigate({
      to: "/bible/$version/$bookAbbr/$chapter",
      params: { version: version(), bookAbbr: bookAbbr(), chapter: chapter() },
      search: (prev) => ({ ...prev, nav: step ? serializeNavStep(step) : undefined }),
      replace: true,
    });
  }

  function openBookSelector() {
    setNavStep({ tab: "book" });
  }

  function handleNavigate(target: ReaderNavTarget) {
    // Navigating to the destination clears `?nav=` (the selector closes) and leaves the reader URL
    // as the sole source; we do not close the popup separately so as not to overwrite
    // this navigation with a second `replace` to the base (canonical Pattern A bug).
    void navigate({
      to: "/bible/$version/$bookAbbr/$chapter",
      params: {
        version: version(),
        bookAbbr: target.abbr,
        chapter: String(target.chapter),
      },
      search: target.verse ? { verse: target.verse } : {},
    });
  }

  // Strong dictionary drawer state. Reflected in the URL (`?strong=`) so the
  // reading with its concordance open is shareable/reloadable; the local signal
  // is the reactive source for rendering and a `createEffect` keeps it in sync
  // with the URL (back/forward + a link pasted in another tab).
  const [selectedStrongCode, setSelectedStrongCodeSignal] = createSignal<string | null>(
    search().strong ?? null,
  );

  function setSelectedStrongCode(code: string | null) {
    setSelectedStrongCodeSignal(code);
    void navigate({
      to: "/bible/$version/$bookAbbr/$chapter",
      params: { version: version(), bookAbbr: bookAbbr(), chapter: chapter() },
      search: (prev) => ({ ...prev, strong: code ?? undefined }),
      replace: true,
    });
  }

  // Version selector dropdown state
  const [showVersionDropdown, setShowVersionDropdown] = createSignal(false);

  /**
   * Translator's note open (`<RF>…</RF>`). Ephemeral reading state, not navigation:
   * it does not travel in the URL (the legacy did not make it linkable either) but
   * closes with the back button like any sheet, via `useBackDismiss` from
   * propio `Dialog`.
   */
  const [translatorNote, setTranslatorNote] = createSignal<string | null>(null);

  // Available bible versions replicated locally via PowerSync
  const versionsQuery = createPowerSyncQuery({
    queryKey: () => queryKeys.bible.versionsLite(),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select({ id: bible_version.id, abbr: bible_version.abbr, name: bible_version.name })
        .from(bible_version);
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()),
  });

  /**
   * Compass-pill label: `Joh 1:7 - KJV`. EXACT format of the published
   * app (3-letter abbreviation + `chapter:verse` + version), which is
   * what users recognize when updating. The verse is the one from the deep link or
   * the first of the selection; without either, 1.
   */
  const compassLabel = () => {
    const abbr = bookAbbr();
    const shortBook = abbr.charAt(0).toUpperCase() + abbr.slice(1).toLowerCase();
    const focusVerse = search().verse ?? readerStore.getSelectedVersesArray()[0] ?? 1;
    return `${shortBook} ${chapter()}:${focusVerse} - ${version().toUpperCase()}`;
  };

  // Cross-book chapter navigation (legacy parity: prev-next-chapter-helper)
  const prevChapterRef = () => getPrevChapter(bookId(), Number.parseInt(chapter(), 10));
  const nextChapterRef = () => getNextChapter(bookId(), Number.parseInt(chapter(), 10));

  function navigateToChapter(ref: { bookId: number; chapter: number } | undefined) {
    if (!ref) {
      return;
    }
    void navigate({
      to: "/bible/$version/$bookAbbr/$chapter",
      params: {
        version: version(),
        bookAbbr: idToAbbr(ref.bookId),
        chapter: String(ref.chapter),
      },
    });
  }

  function handleVersionChange(abbr: string) {
    setShowVersionDropdown(false);
    void navigate({
      to: "/bible/$version/$bookAbbr/$chapter",
      params: {
        version: abbr.toLowerCase(),
        bookAbbr: bookAbbr(),
        chapter: chapter(),
      },
    });
  }

  // Keyboard navigation between chapters (skip while typing or with the
  // book/chapter dialog open)
  function handleGlobalKeyDown(e: KeyboardEvent) {
    if (isBookSelectorOpen()) {
      return;
    }
    const { target } = e;
    if (
      target instanceof HTMLElement &&
      (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
    ) {
      return;
    }
    if (e.key === "ArrowLeft") {
      navigateToChapter(prevChapterRef());
    } else if (e.key === "ArrowRight") {
      navigateToChapter(nextChapterRef());
    }
  }

  // --- Swipe between chapters (carousel) ----------------------------------
  // The reader panel tracks the finger horizontally; on commit (or fling) the
  // current chapter slides out and navigation loads the next/previous one.
  // Tuning lives in READER_SWIPE; the physics (axis-lock, deadzone, fling) come
  // from the shared recognizer, so this matches the gesture standard app-wide.
  // `touch-action: pan-y` keeps native vertical scroll while we claim the
  // horizontal axis. Keyboard nav (← →) is untouched, and reduced-motion users
  // get an instant jump with no slide.
  const [dragX, setDragX] = createSignal(0);
  const [swipeTransitioning, setSwipeTransitioning] = createSignal(false);
  const [swipeRef, setSwipeRef] = createSignal<HTMLDivElement>();

  const panelWidth = () => swipeRef()?.clientWidth ?? READER_SWIPE.fallbackWidthPx;

  /** Animate a directional chapter change. delta>0 = next (slides left). */
  function animateChapter(delta: number) {
    const ref = delta > 0 ? nextChapterRef() : prevChapterRef();
    if (!ref) {
      // No neighbour in that direction: snap back instead of leaving a gap.
      setSwipeTransitioning(true);
      setDragX(0);
      return;
    }
    if (prefersReducedMotion()) {
      setDragX(0);
      navigateToChapter(ref);
      return;
    }
    const width = panelWidth();
    const outX = delta > 0 ? -width : width; // current chapter leaves toward travel
    setSwipeTransitioning(true);
    setDragX(outX);
    vibrate(READER_SWIPE.hapticMs);
    globalThis.setTimeout(() => {
      // Navigation remounts the route content, so reset the transform first.
      setSwipeTransitioning(false);
      setDragX(0);
      navigateToChapter(ref);
    }, READER_SWIPE.transitionMs);
  }

  function snapBack() {
    setSwipeTransitioning(true);
    setDragX(0);
  }

  const swipeRecognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: READER_SWIPE.thresholdPx,
    fling: { ...READER_SWIPE.fling },
    onStart: () => {
      setSwipeTransitioning(false);
    },
    onMove: (sample) => {
      setSwipeTransitioning(false);
      setDragX(sample.dx);
    },
    onEnd: (sample) => {
      const commitPx = Math.min(
        READER_SWIPE.commitThresholdPx,
        panelWidth() * READER_SWIPE.commitThresholdRatio,
      );
      const committed = sample.isFling || Math.abs(sample.dx) >= commitPx;
      if (committed) {
        // Swipe left (dx<0) → next chapter; swipe right (dx>0) → previous.
        animateChapter(sample.dx < 0 ? 1 : -1);
      } else {
        snapBack();
      }
    },
    onCancel: snapBack,
  });

  const swipeHandlers = pointerHandlers(swipeRecognizer);

  // --- Pull-down to open the book/chapter/verse selector ------------------
  // From the top of the reader, dragging downward past the commit threshold
  // opens the nav selector (the inverse of the Dialog's pull-to-dismiss). Shares
  // the same pointer stream as the horizontal chapter swipe; axis-lock means
  // only the gesture matching the drag direction engages. `canEngage` gates it
  // to scrollTop 0 so reading scroll is never hijacked.
  const pullNav = useReaderPullNav({
    getScrollTop: () => swipeRef()?.scrollTop ?? 0,
    prefersReducedMotion,
    onOpen: () => {
      openBookSelector();
    },
  });

  const readerHandlers = mergePointerHandlers(swipeHandlers, pullNav.handlers);

  // Combine horizontal swipe and vertical pull into one transform. Only one axis
  // is ever non-zero at a time (the recognizers axis-lock), so they never fight.
  const swipeTransform = () => `translate3d(${dragX()}px, ${pullNav.pullY()}px, 0)`;
  const swipeTransition = () =>
    swipeTransitioning() || pullNav.settling()
      ? `transform ${READER_SWIPE.transitionMs}ms ease-out`
      : "none";

  // Reference for scroll container to listen to clicks imperatively
  let containerRef: HTMLDivElement | undefined = undefined;

  onMount(() => {
    containerRef?.addEventListener("click", handleChapterClick);
    globalThis.addEventListener("keydown", handleGlobalKeyDown);
  });

  onCleanup(() => {
    containerRef?.removeEventListener("click", handleChapterClick);
    globalThis.removeEventListener("keydown", handleGlobalKeyDown);
    swipeRecognizer.cancel();
    pullNav.cancel();
  });

  createEffect(() => {
    const v = version();
    const b = bookAbbr();
    const c = chapter();
    if (v && b && c) {
      localStorage.setItem(
        "last-read-bible",
        JSON.stringify({ version: v, bookAbbr: b, chapter: c }),
      );
    }
  });

  // Restore the startup deep link (`?strong=`/`?verse=`) if router boot
  // lost it: TanStack's first deferred commit (`flush` in a microtask) can
  // rewrite the URL WITHOUT the search params, and does so AFTER `onMount` on
  // this route. That is why restoring once on mount is not enough: we take the
  // initial snapshot (captured synchronously before the flush) and, through an effect,
  // reapply it while the URL does not yet reflect it. After confirming it is stable
  // for a few ticks (the flush will no longer delete it), we disarm and stop touching
  // the URL → idempotent, and later internal navigations take precedence.
  const bootStrong = parseStrongParam(takeInitialSearchParam("strong", "/bible/") ?? undefined);
  const bootVerseRaw = takeInitialSearchParam("verse", "/bible/");
  const bootVerseNum = bootVerseRaw ? Number(bootVerseRaw) : Number.NaN;
  const bootVerse = Number.isFinite(bootVerseNum) && bootVerseNum > 0 ? bootVerseNum : undefined;
  // Shareable view state that the router flush can also lose when
  // arrancar por deep-link (`?sel=`, `?fs=`, `?strongs=`, `?compose=`).
  const bootSelVerses = parseVerseSelection(takeInitialSearchParam("sel", "/bible/") ?? undefined);
  const bootSel = bootSelVerses.length > 0 ? serializeVerseSelection(bootSelVerses) : undefined;
  const bootFs = parseFontSizeParam(takeInitialSearchParam("fs", "/bible/") ?? undefined);
  // The startup snapshot is read from the RAW URL text, so it arrives as a
  // string ("0"/"1"); we normalize it to the canonical NUMBER 0|1 form so that it
  // matches `search().strongs` exactly in the restore-poll (without that cast,
  // `"0" === 0` would be false and the poll would navigate in a loop — the CC-18 symptom).
  const bootStrongsRaw = takeInitialSearchParam("strongs", "/bible/");
  const bootStrongs = bootStrongsRaw === "0" ? 0 : bootStrongsRaw === "1" ? 1 : undefined;
  const bootCompose = parseComposeParam(takeInitialSearchParam("compose", "/bible/") ?? undefined);
  const bootNavStep = parseNavParam(takeInitialSearchParam("nav", "/bible/") ?? undefined);
  const bootNav = bootNavStep ? serializeNavStep(bootNavStep) : undefined;
  if (
    bootStrong !== undefined ||
    bootVerse !== undefined ||
    bootSel !== undefined ||
    bootFs !== undefined ||
    bootStrongs !== undefined ||
    bootCompose !== undefined ||
    bootNav !== undefined
  ) {
    // Short restoration window after mount: reapply the startup deep link
    // while the URL does not reflect it, because the flush deletes it in
    // a microtask AFTER the first effect. We poll every 150 ms until the
    // URL already carries the parameter (and settles) or the window expires; then polling
    // ends and the snapshot stops taking precedence (closing the panel, etc. is free).
    const RESTORE_POLL_MS = 150;
    const RESTORE_MAX_TICKS = 14; // ~2.1 s
    const [restoreTick, setRestoreTick] = createSignal(0);
    // Consecutive confirmations that the URL already reflects the deep link. We require
    // several in a row to ensure the router flush (which deletes the
    // parameters in a microtask) has already passed and will not clobber them again. Only then
    // do we disarm, so closing the panel later does not reopen it.
    const REQUIRED_STABLE_TICKS = 3;
    let stableCount = 0;
    let disarmed = false;
    createEffect(() => {
      const tick = restoreTick();
      if (disarmed) {
        return;
      }
      const current = search();
      const strongMatches = bootStrong === undefined || current.strong === bootStrong;
      const verseMatches = bootVerse === undefined || current.verse === bootVerse;
      const selMatches = bootSel === undefined || current.sel === bootSel;
      const fsMatches = bootFs === undefined || current.fs === bootFs;
      const strongsMatches = bootStrongs === undefined || current.strongs === bootStrongs;
      const composeMatches = bootCompose === undefined || current.compose === bootCompose;
      const navMatches = bootNav === undefined || current.nav === bootNav;
      if (
        strongMatches &&
        verseMatches &&
        selMatches &&
        fsMatches &&
        strongsMatches &&
        composeMatches &&
        navMatches
      ) {
        stableCount += 1;
        if (stableCount >= REQUIRED_STABLE_TICKS) {
          disarmed = true;
          return;
        }
      } else {
        stableCount = 0;
        void navigate({
          to: "/bible/$version/$bookAbbr/$chapter",
          params: { version: version(), bookAbbr: bookAbbr(), chapter: chapter() },
          search: (prev) => ({
            ...prev,
            ...(bootStrong ? { strong: bootStrong } : {}),
            ...(bootVerse ? { verse: bootVerse } : {}),
            ...(bootSel ? { sel: bootSel } : {}),
            ...(bootFs ? { fs: bootFs } : {}),
            ...(bootStrongs === undefined ? {} : { strongs: bootStrongs }),
            ...(bootCompose ? { compose: bootCompose } : {}),
            ...(bootNav ? { nav: bootNav } : {}),
          }),
          replace: true,
        });
      }
      if (tick >= RESTORE_MAX_TICKS) {
        disarmed = true;
        return;
      }
      const id = setTimeout(() => {
        setRestoreTick((n) => n + 1);
      }, RESTORE_POLL_MS);
      onCleanup(() => {
        clearTimeout(id);
      });
    });
  }

  // Reflects `?strong=` (URL) → local signal: opens the panel when loading by URL,
  // back/forward navigation, or a pasted link. Idempotent: the URL value
  // wins; without it, the panel stays closed.
  createEffect(() => {
    const code = search().strong ?? null;
    if (code !== selectedStrongCode()) {
      setSelectedStrongCodeSignal(code);
    }
  });

  // --- Reader view state reflected in the URL ---------------------
  // Verse selection (`?sel=`), font size (`?fs=`), and Strong
  // visibility (`?strongs=`) are now **shareable**: the URL value wins
  // (URL→store), and header toggles write to the URL (store→URL via
  // `replace`). Thus pasting/reloading the URL reproduces the screen in another browser.

  /** Writes view state to the URL without bloating history. */
  function patchReaderSearch(patch: Partial<BibleSearch>) {
    void navigate({
      to: "/bible/$version/$bookAbbr/$chapter",
      params: { version: version(), bookAbbr: bookAbbr(), chapter: chapter() },
      search: (prev) => ({ ...prev, ...patch }),
      replace: true,
    });
  }

  // URL `?sel=` → store. The URL value is the source of truth for the selection.
  createEffect(() => {
    const fromUrl = parseVerseSelection(search().sel);
    const inStore = readerStore.getSelectedVersesArray();
    // Compare by content to avoid re-setting (and breaking reactivity) in a loop.
    const equal = fromUrl.length === inStore.length && fromUrl.every((v, i) => v === inStore[i]);
    if (!equal) {
      readerStore.setSelection(fromUrl);
    }
  });

  // URL `?fs=` → store (if different). Without the parameter, the local preference wins.
  createEffect(() => {
    const { fs } = search();
    if (fs && fs !== readerStore.fontSize) {
      readerStore.setFontSize(fs);
    }
  });

  // URL `?strongs=` → store (if different). Without the parameter, the local preference wins.
  createEffect(() => {
    const raw = search().strongs;
    if (raw !== undefined) {
      const show = raw === 1;
      if (show !== readerStore.showStrongs) {
        readerStore.setShowStrongs(show);
      }
    }
  });

  // The editor (`?compose=`) is valid only when verses are selected; when
  // the selection is cleared, discard compose so the URL does not remain inconsistent.
  const composeMode = (): ReaderCompose | undefined =>
    readerStore.getSelectedVersesArray().length > 0 ? search().compose : undefined;

  // Verses of the chapter, OFFLINE-FIRST: base PowerSync local → Tauri nativo →
  // HTTP API. The MVP source is the local seed (bible_verse_content).
  const [verses] = createResource(
    () => ({
      version: version(),
      book: bookId(),
      chapter: Number.parseInt(chapter(), 10),
      // Re-run once the Drizzle client is ready (the seed needs the DB).
      ready: Boolean(drizzle()),
    }),
    // While the Drizzle client is still opening there is nothing to read
    // locally; returning [] lets the resource re-run when `ready` flips
    // instead of sending the chapter request to the server (which 404s for a
    // version it does not host and logs a console error). `loadChapter` seeds
    // the local database before falling back to the network.
    async (query) => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return [];
      }
      return loadChapter(dbInstance, query.version, query.book, query.chapter);
    },
  );

  // Resolves the chapter CONTENT (the reference is already valid): present /
  // loading / not-found / offline. A valid reference whose text has not yet
  // been downloaded/seeded (e.g. a version not replicated while offline) previously
  // left the reader blank; here we distinguish "offline, not yet synced" from "no
  // content for this version" instead of showing a misleading empty state.
  const contentResolution = createEntityResolution({
    db: () => db(),
    hasEntity: () => (verses()?.length ?? 0) > 0,
    isPending: () => verses.loading,
  });

  // Scroll to targeted verse if provided in search params. Only scrolls; no longer
  // directly mutates the selection (that is now governed by `?sel=`). If the deep
  // link did not contain `?sel=`, seed the target verse selection into the
  // URL so it is highlighted and consistently shareable.
  createEffect(() => {
    const target = search().verse;
    if (target && !verses.loading) {
      if (search().sel === undefined) {
        patchReaderSearch({ sel: serializeVerseSelection([target]) });
      }
      setTimeout(() => {
        const el = document.querySelector(`#verse-${target}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, SCROLL_DELAY_MS);
    }
  });

  // Query highlights reactively from PowerSync local instance using exact numeric bookId
  const highlights = createPowerSyncQuery({
    queryKey: () => queryKeys.bible.chapterHighlights(version(), bookId(), chapter(), userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = userId();
      const bId = bookId();
      const chNum = Number.parseInt(chapter(), 10);
      if (!dbInstance || !u || Number.isNaN(bId) || Number.isNaN(chNum)) {
        return null;
      }
      return dbInstance
        .select({
          id: bible_verse_highlight.id,
          verse: bible_verse_highlight.verse,
          color: bible_verse_highlight.color,
        })
        .from(bible_verse_highlight)
        .where(
          and(
            eq(bible_verse_highlight.user_id, u),
            eq(bible_verse_highlight.version, version()),
            eq(bible_verse_highlight.book, bId),
            eq(bible_verse_highlight.chapter, chNum),
            isNull(bible_verse_highlight.deleted_at),
          ),
        );
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()) && Boolean(userId()),
  });

  // Fast O(1) index lookup map for highlights
  const highlightMap = createMemo(() => {
    const map = new Map<number, string>();
    const list = highlights.data ?? [];
    for (const h of list) {
      if (h.verse !== null && h.color) {
        map.set(h.verse, h.color);
      }
    }
    return map;
  });

  /**
   * Highlight blocks: which corners each verse rounds so
   * adjacent verses of the same color read as ONE block, as in the
   * published app. Pure logic in `@/utils/highlight-blocks` (with its own test).
   */
  const highlightBlocks = createMemo(() =>
    computeHighlightBlocks(
      (verses() ?? []).flatMap((v) => (typeof v.verse === "number" ? [v.verse] : [])),
      highlightMap(),
    ),
  );

  // Bookmarks/favorites of this chapter (personal user data, synced via PowerSync)
  const bookmarks = createPowerSyncQuery({
    queryKey: () => queryKeys.bible.chapterBookmarks(version(), bookId(), chapter(), userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = userId();
      const bId = bookId();
      const chNum = Number.parseInt(chapter(), 10);
      if (!dbInstance || !u || Number.isNaN(bId) || Number.isNaN(chNum)) {
        return null;
      }
      return dbInstance
        .select({ verse: bible_bookmark.verse })
        .from(bible_bookmark)
        .where(
          and(
            eq(bible_bookmark.user_id, u),
            eq(bible_bookmark.version, version()),
            eq(bible_bookmark.book, bId),
            eq(bible_bookmark.chapter, chNum),
            isNull(bible_bookmark.deleted_at),
          ),
        );
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()) && Boolean(userId()),
  });

  // Fast O(1) lookup set for the bookmark indicator and the toggle decision
  const bookmarkSet = createMemo(() => {
    const set = new Set<number>();
    for (const b of bookmarks.data ?? []) {
      if (b.verse !== null) {
        set.add(b.verse);
      }
    }
    return set;
  });

  // Event delegation handler for Strong's clicks
  function handleChapterClick(e: MouseEvent) {
    const { target } = e;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    const strongCode = target.dataset.strong;

    if (strongCode) {
      e.stopPropagation();
      setSelectedStrongCode(strongCode);
    }
  }

  // Handler to toggle verse selection state. Writes to URL (`?sel=`) —
  // source of truth—so the selection is shareable/reloadable; the
  // URL→store effect updates the render. When the selection is cleared, we also
  // discard `?compose=` (an editor without verses makes no sense).
  function handleVerseClick(verseNumber: number) {
    const current = new Set(readerStore.getSelectedVersesArray());
    if (current.has(verseNumber)) {
      current.delete(verseNumber);
    } else {
      current.add(verseNumber);
    }
    const next = serializeVerseSelection([...current]);
    patchReaderSearch({
      sel: next === "" ? undefined : next,
      ...(next === "" ? { compose: undefined } : {}),
    });
  }

  // Listen for Escape key to close dictionary drawer
  createEffect(() => {
    if (selectedStrongCode()) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          setSelectedStrongCode(null);
        }
      };
      globalThis.addEventListener("keydown", handleKeyDown);
      onCleanup(() => {
        globalThis.removeEventListener("keydown", handleKeyDown);
      });
    }
  });

  return (
    <Show
      when={validReference()}
      fallback={
        <div class="flex h-full w-full items-center justify-center bg-background text-foreground">
          <NotFoundState
            variant="not-found"
            title={t(msg`Passage not found`)}
            description={t(
              msg`That book or chapter doesn't exist. Check the reference and try again.`,
            )}
            primaryAction={{
              label: t(msg`Go to Genesis 1`),
              onClick: () => {
                void navigate({
                  to: "/bible/$version/$bookAbbr/$chapter",
                  params: { version: version(), bookAbbr: "gen", chapter: "1" },
                });
              },
            }}
          />
        </div>
      }
    >
      <div class="flex h-full w-full overflow-hidden bg-background text-foreground relative">
        {/* Book → Chapter → Verse navigation selector (reader feature) */}
        <ReaderNavSelector
          open={isBookSelectorOpen()}
          step={navStep()}
          onStepChange={setNavStep}
          onClose={() => {
            setNavStep(undefined);
          }}
          version={version()}
          currentBookAbbr={bookAbbr()}
          currentChapter={Number.parseInt(chapter(), 10)}
          db={drizzle()}
          onNavigate={handleNavigate}
        />

        {/* Main Bible Reader Panel */}
        <div class="flex flex-1 flex-col overflow-hidden relative">
          {/* Reader Top Bar */}
          <div class="flex items-center border-b border-border/40 bg-card/35 backdrop-blur-lg px-2 sm:px-4 md:px-6 py-2.5 md:py-3 shrink-0 gap-1.5 md:gap-2">
            <div class="flex items-center gap-1.5 md:gap-2 shrink-0">
              <button
                type="button"
                onClick={() => {
                  navigateToChapter(prevChapterRef());
                }}
                disabled={!prevChapterRef()}
                data-testid="bar-prev-chapter"
                class="flex items-center justify-center min-h-11 min-w-11 p-2 rounded-xl bg-muted/40 hover:bg-muted/70 text-foreground border border-border/50 transition-all active:scale-95 disabled:opacity-30 disabled:pointer-events-none shrink-0"
                title={t(msg`Previous chapter (←)`)}
                aria-label={t(msg`Previous chapter`)}
              >
                <ChevronLeft class="w-4 h-4" />
              </button>
            </div>

            {/* CENTERED compass pill: `Joh 1:7 - KJV`. It is the element that the
              usuario de la app publicada busca con la mirada para saber dónde
              está; iba centrada y con versículo y versión. Sigue abriendo el
              selector de navegación y conservando su deep-link (`?nav=`). */}
            <div class="flex flex-1 min-w-0 items-center justify-center">
              <button
                type="button"
                onClick={openBookSelector}
                data-testid="reader-nav-trigger"
                class="flex items-center gap-1.5 px-5 py-1.5 min-h-11 rounded-2xl bg-muted/40 hover:bg-muted/70 text-foreground font-bold transition-all border border-border/50 text-sm active:scale-95 min-w-0 max-w-full"
                aria-label={t(msg`Browse books and chapters`)}
              >
                <span class="truncate tabular-nums" data-testid="reader-compass-label">
                  {compassLabel()}
                </span>
                <ChevronDown class="w-4 h-4 text-muted-foreground shrink-0" />
              </button>
            </div>

            <div class="flex items-center gap-1.5 md:gap-2 shrink-0">
              {/* Bible version selector */}
              <div class="relative">
                <button
                  type="button"
                  onClick={() => setShowVersionDropdown(!showVersionDropdown())}
                  class="flex items-center justify-center gap-1 md:gap-1.5 px-2.5 md:px-3 py-2 min-h-11 min-w-11 rounded-xl bg-muted/40 hover:bg-muted/70 text-foreground font-bold text-2xs md:text-xs border border-border/50 transition-all active:scale-95"
                  title={t(msg`Change Bible version`)}
                >
                  <span>{version().toUpperCase()}</span>
                  <ChevronDown class="w-3.5 h-3.5 text-muted-foreground hidden sm:block" />
                </button>

                <Show when={showVersionDropdown()}>
                  <div class="absolute right-0 mt-2 w-56 max-h-72 overflow-y-auto bg-card border border-border/60 rounded-xl shadow-lg z-50 p-2">
                    <span class="block px-2 pb-2 border-b border-border/20 mb-1 text-3xs text-muted-foreground font-semibold uppercase">
                      {t(msg`Bible Version`)}
                    </span>
                    <For
                      each={versionsQuery.data ?? []}
                      fallback={
                        <p class="px-3 py-2 text-xs text-muted-foreground">
                          {t(msg`No versions synchronized.`)}
                        </p>
                      }
                    >
                      {(v) => (
                        <button
                          type="button"
                          onClick={() => {
                            if (v.abbr) {
                              handleVersionChange(v.abbr);
                            }
                          }}
                          class="w-full text-left px-3 py-1.5 text-xs rounded-lg hover:bg-muted/40 font-medium transition-colors flex items-center justify-between gap-2"
                          classList={{
                            "bg-primary/10 text-primary font-semibold":
                              v.abbr?.toLowerCase() === version(),
                            "text-foreground/80": v.abbr?.toLowerCase() !== version(),
                          }}
                        >
                          <span class="font-bold">{v.abbr?.toUpperCase()}</span>
                          <span class="text-muted-foreground truncate">{v.name}</span>
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
              </div>

              {/* Font size cycle (legacy parity: FontSizeToggleButton).
                Escribe el resultado a la URL (`?fs=`) para que el modo de vista
                sea compartible; el efecto URL→store aplica el cambio. */}
              <button
                type="button"
                onClick={() => {
                  const nextSize: Record<ReaderFontSize, ReaderFontSize> = {
                    small: "medium",
                    medium: "large",
                    large: "small",
                  };
                  patchReaderSearch({ fs: nextSize[readerStore.fontSize] });
                }}
                class="flex items-center justify-center min-h-11 min-w-11 p-2 rounded-xl bg-muted/40 hover:bg-muted/70 text-foreground border border-border/50 transition-all active:scale-95"
                title={(() => {
                  const size = readerStore.fontSize;
                  return t(msg`Font size: ${size}`);
                })()}
                aria-label={t(msg`Change font size`)}
              >
                <ALargeSmall class="w-4 h-4" />
              </button>

              {/* Strong numbers visibility toggle (legacy parity: CodeStringIcon
                header button). Escribe a la URL la forma canónica NÚMERO
                (`?strongs=0` oculto / `1` visible) → deep-link limpio y
                compartible; el efecto URL→store aplica el cambio. */}
              <button
                type="button"
                onClick={() => {
                  patchReaderSearch({ strongs: readerStore.showStrongs ? 0 : 1 });
                }}
                class="flex items-center justify-center min-h-11 min-w-11 p-2 rounded-xl border transition-all active:scale-95"
                classList={{
                  "bg-primary/15 border-primary/40 text-primary": readerStore.showStrongs,
                  "bg-muted/40 hover:bg-muted/70 border-border/50 text-muted-foreground":
                    !readerStore.showStrongs,
                }}
                title={
                  readerStore.showStrongs
                    ? t(msg`Hide Strong numbers`)
                    : t(msg`Show Strong numbers`)
                }
                aria-label={t(msg`Toggle Strong numbers`)}
                aria-pressed={readerStore.showStrongs}
              >
                <Superscript class="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={() => {
                  navigateToChapter(nextChapterRef());
                }}
                disabled={!nextChapterRef()}
                data-testid="bar-next-chapter"
                class="flex items-center justify-center min-h-11 min-w-11 p-2 rounded-xl bg-muted/40 hover:bg-muted/70 text-foreground border border-border/50 transition-all active:scale-95 disabled:opacity-30 disabled:pointer-events-none shrink-0"
                title={t(msg`Next chapter (→)`)}
                aria-label={t(msg`Next chapter`)}
              >
                <ChevronRight class="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Reader Content / Scroll Container.
            Owns the horizontal swipe-between-chapters gesture: `touch-action:
            pan-y` keeps native vertical scroll while the recognizer claims the
            horizontal axis (preventDefaults engaged moves). */}
          <div
            ref={(el) => {
              containerRef = el;
              setSwipeRef(el);
            }}
            class="flex-1 overflow-y-auto px-3 py-5 sm:p-6 md:p-8 relative"
            style={{ "touch-action": "pan-y" }}
            {...readerHandlers}
          >
            {/* Pull-down hint: fades in as the page is dragged down from the top,
              telling the reader the release will open the nav selector. */}
            <Show when={pullNav.pullY() > 4}>
              <div
                class="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground select-none"
                style={{
                  opacity: String(Math.min(1, pullNav.pullY() / 72)),
                  transform: `translateY(${Math.min(pullNav.pullY() * 0.5, 28)}px)`,
                }}
              >
                <ChevronDown class="w-3 h-3" />
                <span>{t(msg`Pull to browse`)}</span>
              </div>
            </Show>
            <div
              class="max-w-3xl mx-auto w-full space-y-1 pb-24 will-change-transform"
              role="listbox"
              aria-multiselectable="true"
              aria-label={t(msg`Chapter verses`)}
              style={{ transform: swipeTransform(), transition: swipeTransition() }}
            >
              <Show
                when={contentResolution() !== "loading"}
                fallback={
                  <div class="space-y-4 animate-pulse">
                    <div class="h-4 bg-muted/65 rounded w-3/4" />
                    <div class="h-4 bg-muted/65 rounded w-5/6" />
                    <div class="h-4 bg-muted/65 rounded w-2/3" />
                    <div class="h-4 bg-muted/65 rounded w-4/5" />
                  </div>
                }
              >
                <Show
                  when={contentResolution() === "ready"}
                  fallback={
                    <NotFoundState
                      variant={contentResolution() === "offline" ? "offline" : "not-found"}
                      title={
                        contentResolution() === "offline"
                          ? undefined
                          : t(msg`Passage not available`)
                      }
                      description={
                        contentResolution() === "offline"
                          ? undefined
                          : t(
                              msg`This chapter hasn't been downloaded for this version yet. Try another version or download it in Settings.`,
                            )
                      }
                      primaryAction={{
                        label: t(msg`Open settings`),
                        onClick: () => {
                          void navigate({ to: "/settings" });
                        },
                      }}
                    />
                  }
                >
                  {/* Centered chapter header `KJV / John / Chapter 1`
                    (paridad con `PrintVerseListHeader` de la app publicada). */}
                  <header class="text-center pt-6 pb-8 select-none">
                    <p class="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                      {version().toUpperCase()}
                    </p>
                    <h1 class="font-serif text-4xl font-bold text-foreground leading-tight mt-1">
                      {bookName()}
                    </h1>
                    <p class="text-sm text-muted-foreground mt-0.5">
                      {(() => {
                        const chapterNumber = chapter();
                        return t(msg`Chapter ${chapterNumber}`);
                      })()}
                    </p>
                  </header>

                  <For each={verses()}>
                    {(verse) => {
                      const block = () =>
                        highlightBlocks().get(verse.verse) ?? {
                          color: null,
                          roundTop: true,
                          roundBottom: true,
                        };
                      // Inside a full block, the ink is determined by the
                      // pastel luminance (equivalent to `Colors.isDark`);
                      // outside, the theme color. This also applies to the Strong blue
                      // and the red of Jesus's words: in dark mode their light variants
                      // would be illegible on a pastel, which remains light.
                      // pastel, which remains light.
                      const ink = () => {
                        const c = block().color;
                        return c ? readingInkOn(c) : null;
                      };
                      const inkColor = () => ink()?.text;
                      return (
                        <div
                          id={`verse-${verse.verse}`}
                          data-highlighted={block().color ? "true" : undefined}
                          onClick={() => {
                            handleVerseClick(verse.verse);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === " " || e.key === "Enter") {
                              e.preventDefault();
                              handleVerseClick(verse.verse);
                            }
                          }}
                          role="option"
                          aria-selected={Boolean(readerStore.selectedVerses[verse.verse])}
                          tabindex="0"
                          class="group relative flex items-start gap-1 py-3 px-4 border border-transparent transition-[background-color,box-shadow] duration-200 cursor-pointer select-none outline-none"
                          classList={{
                            "ring-2 ring-primary/40 shadow-sm": Boolean(
                              readerStore.selectedVerses[verse.verse],
                            ),
                            "bg-primary/8":
                              Boolean(readerStore.selectedVerses[verse.verse]) && !block().color,
                            "hover:bg-muted/15":
                              !readerStore.selectedVerses[verse.verse] && !block().color,
                          }}
                          style={{
                            // Highlight as a SOLID BACKGROUND; adjacent
                            // verses of the same color form one block
                            // (square corners where they meet).
                            "background-color": block().color ?? undefined,
                            color: inkColor(),
                            "border-top-left-radius": block().roundTop ? "0.75rem" : "0",
                            "border-top-right-radius": block().roundTop ? "0.75rem" : "0",
                            "border-bottom-left-radius": block().roundBottom ? "0.75rem" : "0",
                            "border-bottom-right-radius": block().roundBottom ? "0.75rem" : "0",
                            "margin-top":
                              block().color && !block().roundTop ? "-0.25rem" : undefined,
                          }}
                        >
                          <span
                            class="align-super text-xs font-bold mr-2.5 select-none shrink-0 mt-0.5 flex items-center gap-0.5 tabular-nums"
                            classList={{ "text-accent": !block().color }}
                            style={{ color: inkColor() }}
                          >
                            {verse.verse}
                            <Show when={bookmarkSet().has(verse.verse)}>
                              <Bookmark
                                class="w-3 h-3 fill-warning text-warning"
                                data-testid={`bookmark-indicator-${verse.verse}`}
                              />
                            </Show>
                          </span>
                          <p
                            class="leading-[1.85] tracking-[0.003em] flex-1 font-serif select-text"
                            classList={{
                              "text-foreground/95": !block().color,
                              // Legacy typographic scale: 18 / 22 / 26 px.
                              // Reducing it would make the update feel like
                              // "me han encogido la letra".
                              "text-[1.125rem]": readerStore.fontSize === "small",
                              "text-[1.375rem]": readerStore.fontSize === "medium",
                              "text-[1.625rem]": readerStore.fontSize === "large",
                            }}
                          >
                            <For each={formatVerseText(verse.text, readerStore.showStrongs)}>
                              {(token) => {
                                if (token.type === "strong") {
                                  // Inline, at BODY size and in blue: this is
                                  // how it looked in the published app. The previous gold
                                  // superscript at 10 px gave 3,03:1 (failed
                                  // WCAG AA); el azul da 9,8:1.
                                  return (
                                    <span
                                      class="text-strong font-semibold cursor-pointer mx-0.5 hover:underline focus-visible:underline select-none dict-link"
                                      style={{ color: ink()?.strong }}
                                      data-strong={token.code}
                                    >
                                      {token.label}
                                    </span>
                                  );
                                }
                                if (token.type === "note") {
                                  return (
                                    <button
                                      type="button"
                                      class="inline-flex align-baseline items-center mx-0.5 text-muted-foreground hover:text-accent transition-colors"
                                      data-testid="translator-note"
                                      title={t(msg`Translator's note`)}
                                      aria-label={t(msg`Translator's note`)}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setTranslatorNote(token.text);
                                      }}
                                    >
                                      <StickyNote class="w-3.5 h-3.5" />
                                    </button>
                                  );
                                }
                                if (token.italic || token.redLetter) {
                                  // `<FI>` = word supplied by the translator
                                  // (italics, KJV convention). `<FR>` = words
                                  // of Jesus (red letters).
                                  return (
                                    <span
                                      classList={{
                                        italic: token.italic,
                                        "text-red-letter": token.redLetter,
                                      }}
                                      style={
                                        token.redLetter ? { color: ink()?.redLetter } : undefined
                                      }
                                    >
                                      {token.text}
                                    </span>
                                  );
                                }
                                return <span>{token.text}</span>;
                              }}
                            </For>
                          </p>
                        </div>
                      );
                    }}
                  </For>
                </Show>
              </Show>
            </div>
          </div>

          {/* Pair of floating chapter buttons ‹ ›, the most
            reconocible del lector publicado (capturas 02/03): redondos, abajo a
            izquierda y derecha, sobre el texto. Se OCULTAN al seleccionar
            versículos (misma señal que abre la hoja de acciones) y cuando no hay
            capítulo destino — el legacy no mostraba botones muertos.
            `start`/`end` en vez de `left`/`right`: en árabe y hebreo el lector
            se lee al revés y el "anterior" debe quedar del lado correcto.

            A11Y — `aria-hidden` + `tabindex="-1"` NO es una omisión: estos dos
            botones DUPLICAN exactamente la acción y el nombre accesible de los
            de la barra superior (`bar-prev-chapter` / `bar-next-chapter`), y
            ambos pares son visibles A LA VEZ en desktop. Dos controles
            operables con el MISMO nombre accesible ("Next chapter") y el mismo
            efecto son ambiguos para un lector de pantalla (los anuncia dos
            veces, sin forma de distinguirlos) y meten una parada de tabulador
            redundante. Al ser un atajo puramente visual/táctil sobre el texto,
            la forma correcta es dejarlos fuera del árbol de accesibilidad y del
            orden de tabulación: el control canónico de la barra sigue expuesto,
            con su atajo de teclado (←/→), así que el usuario de AT no pierde
            NINGUNA funcionalidad. Quitar `tabindex="-1"` junto con
            `aria-hidden` sería la violación `aria-hidden-focus`. */}
          <Show when={readerStore.getSelectedVersesArray().length === 0}>
            <Show when={prevChapterRef()}>
              <button
                type="button"
                data-testid="float-prev-chapter"
                onClick={() => {
                  navigateToChapter(prevChapterRef());
                }}
                class="absolute z-20 flex items-center justify-center w-12 h-12 rounded-full bg-muted/70 backdrop-blur border border-border/50 text-foreground shadow-lg active:scale-95 motion-safe:transition-opacity motion-safe:duration-150 hover:bg-muted"
                style={{
                  bottom: "calc(1.25rem + var(--safe-area-inset-bottom))",
                  "inset-inline-start": "1.25rem",
                }}
                title={t(msg`Previous chapter (←)`)}
                aria-label={t(msg`Previous chapter`)}
                aria-hidden="true"
                tabindex="-1"
              >
                <ChevronLeft class="w-6 h-6" />
              </button>
            </Show>
            <Show when={nextChapterRef()}>
              <button
                type="button"
                data-testid="float-next-chapter"
                onClick={() => {
                  navigateToChapter(nextChapterRef());
                }}
                class="absolute z-20 flex items-center justify-center w-12 h-12 rounded-full bg-muted/70 backdrop-blur border border-border/50 text-foreground shadow-lg active:scale-95 motion-safe:transition-opacity motion-safe:duration-150 hover:bg-muted"
                style={{
                  bottom: "calc(1.25rem + var(--safe-area-inset-bottom))",
                  "inset-inline-end": "1.25rem",
                }}
                title={t(msg`Next chapter (→)`)}
                aria-label={t(msg`Next chapter`)}
                aria-hidden="true"
                tabindex="-1"
              >
                <ChevronRight class="w-6 h-6" />
              </button>
            </Show>
          </Show>

          {/* Nota del traductor (`<RF>`) */}
          <Dialog
            open={translatorNote() !== null}
            onOpenChange={(open) => {
              if (!open) {
                setTranslatorNote(null);
              }
            }}
            contentClass="max-w-md"
          >
            <DialogHeader>
              <DialogTitle class="text-sm font-bold uppercase tracking-widest opacity-60">
                {t(msg`Translator's note`)}
              </DialogTitle>
            </DialogHeader>
            <p class="font-serif text-base leading-relaxed text-foreground/90 px-1 pb-2">
              {translatorNote()}
            </p>
          </Dialog>

          {/* Selected Verses Action Bar */}
          <VerseActionsBar
            userId={userId()}
            version={version()}
            bookId={bookId()}
            bookAbbr={bookAbbr()}
            chapter={Number.parseInt(chapter(), 10)}
            bookName={bookName()}
            verses={verses() ?? []}
            drizzle={drizzle()}
            bookmarkedVerses={bookmarkSet()}
            highlightMap={highlightMap()}
            compose={composeMode()}
            onComposeChange={(next) => {
              patchReaderSearch({ compose: next ?? undefined });
            }}
            onClearSelection={() => {
              patchReaderSearch({ sel: undefined, compose: undefined });
            }}
          />
        </div>

        {/* Responsive Strong Concordance Panel (reader feature) */}
        <Show when={selectedStrongCode()}>
          {(code) => (
            <AppErrorBoundary variant="region" scope="strong-panel">
              <StrongConcordancePanel
                code={code()}
                db={drizzle()}
                onClose={() => setSelectedStrongCode(null)}
                onSelectCode={setSelectedStrongCode}
              />
            </AppErrorBoundary>
          )}
        </Show>
      </div>
    </Show>
  );
}
